import { trackEvent } from '@raisin/events-tracking';
import { getSelectedProductTab } from 'components/ProductsTabs/tabsConfigUtils';
import { MICROSERVICES_NAMES } from 'api/types';
import {
  CUSTOMER_PORTAL_COMPONENT,
  CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
} from 'eventsTracking/constants';
import { CustomerPortalEvent } from 'eventsTracking/types';

/**
 * This file is the single source of truth for all tracking-events of this CUSTOMER PORTAL zone.
 *
 * All events MUST be included here and the custom-attributes/dynamic data can be overridden
 * whenever necessary in the place of event usage.
 *
 */

enum OFFER_TABLE_STATES {
  LOGGED_IN = 'Logged-in',
  NOT_LOGGED_IN = 'Not logged-in',
}

const getLoggedInState = (isLoggedIn: boolean) =>
  isLoggedIn ? OFFER_TABLE_STATES.LOGGED_IN : OFFER_TABLE_STATES.NOT_LOGGED_IN;
/**
 * Method to create all the Offer table events.
 *
 * @param distributorId Distributor ID
 * @param isLoggedIn Whether the user's loggedIn or not
 *
 * @returns All offer table events.
 */
export const createOfferTableEvents = ({ isLoggedIn, distributorId }) => ({
  PRODUCTS_RENDER_DURATION: ({ pageUrl, renderStartTime, renderEndTime, renderDuration }) => {
    const OFFERS_SERVICE = MICROSERVICES_NAMES.DBS;
    return {
      ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
      component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
      eventName: 'offers_table_rendered',
      object: 'offers_table',
      action: 'rendered',
      description: 'Time to render the offers table',
      customerGroup: distributorId,
      customAttribute1: getLoggedInState(isLoggedIn),
      customAttribute2: pageUrl,
      customAttribute3: `render_started_${renderStartTime}`,
      customAttribute4: `${OFFERS_SERVICE}_products_loaded_${renderEndTime}`,
      customAttribute5: `render_duration_${renderDuration}`,
    } as CustomerPortalEvent;
  },
  CTA_CLICKED: ({ productId, investmentAmount }) =>
    ({
      ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
      eventName: 'CTA_clicked',
      component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
      object: 'CTA',
      action: 'clicked',
      productId,
      customerGroup: distributorId,
      customAttribute1: getLoggedInState(isLoggedIn),
      customAttribute2: investmentAmount,
    } as CustomerPortalEvent),

  // ===================================================== //
  //          OFFER TABLE - OFFER ROW
  // ===================================================== //
  OFFER_ROW: {
    DETAILS_CLICKED: ({ productId }) =>
      ({
        ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
        component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
        eventName: 'offer_row_details_clicked',
        object: 'offer_row_details',
        action: 'clicked',
        productId,
        customerGroup: distributorId,
        customAttribute1: getLoggedInState(isLoggedIn),
      } as CustomerPortalEvent),

    BANK_CLICKED: ({ productId }) =>
      ({
        ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
        component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
        eventName: 'offer_row_bank_clicked',
        object: 'offer_row_bank',
        action: 'clicked',
        productId,
        customerGroup: distributorId,
        customAttribute1: getLoggedInState(isLoggedIn),
      } as CustomerPortalEvent),

    GUARANTEE_CLICKED: ({ productId }) =>
      ({
        ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
        component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
        eventName: 'offer_row_guarantee_clicked',
        object: 'offer_row_guarantee',
        action: 'clicked',
        productId,
        customerGroup: distributorId,
        customAttribute1: getLoggedInState(isLoggedIn),
      } as CustomerPortalEvent),
  },

  // ===================================================== //
  //          OFFER TABLE -  TYPES OF PRODUCT
  // ===================================================== //
  PRODUCT_TYPE: {
    TERM_CLICKED: {
      ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
      component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
      eventName: 'product_type_term_clicked',
      object: 'product_type_term',
      action: 'clicked',
      customerGroup: distributorId,
      customAttribute1: getLoggedInState(isLoggedIn),
    } as CustomerPortalEvent,

    FLEXGELD_CLICKED: {
      ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
      component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
      eventName: 'product_type_flexgeld_clicked',
      object: 'product_type_flexgeld',
      action: 'clicked',
      customerGroup: distributorId,
      customAttribute1: getLoggedInState(isLoggedIn),
    } as CustomerPortalEvent,

    OVERNIGHT_CLICKED: {
      ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
      component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
      eventName: 'product_type_overnight_clicked',
      object: 'product_type_overnight',
      action: 'clicked',
      customerGroup: distributorId,
      customAttribute1: getLoggedInState(isLoggedIn),
    } as CustomerPortalEvent,

    NOTICE_CLICKED: {
      ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
      component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
      eventName: 'product_type_notice_clicked',
      object: 'product_type_notice',
      action: 'clicked',
      customerGroup: distributorId,
      customAttribute1: getLoggedInState(isLoggedIn),
    } as CustomerPortalEvent,

    INVEST_CLICKED: {
      ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
      component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
      eventName: 'product_type_invest_clicked',
      object: 'product_type_invest',
      action: 'clicked',
      customerGroup: distributorId,
      customAttribute1: getLoggedInState(isLoggedIn),
    } as CustomerPortalEvent,

    PENSION_CLICKED: {
      ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
      component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
      eventName: 'product_type_pension_clicked',
      object: 'product_type_pension',
      action: 'clicked',
      customerGroup: distributorId,
      customAttribute1: getLoggedInState(isLoggedIn),
    } as CustomerPortalEvent,
  },

  // ===================================================== //
  //          OFFER TABLE -  LOAD MORE OFFERS
  // ===================================================== //
  LOAD_MORE: {
    TERM_CLICKED: {
      ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
      component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
      eventName: 'load_more_term_clicked',
      object: 'load_more_term',
      action: 'clicked',
      customerGroup: distributorId,
      customAttribute1: getLoggedInState(isLoggedIn),
    } as CustomerPortalEvent,

    OVERNIGHT_CLICKED: {
      ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
      component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
      eventName: 'load_more_overnight_clicked',
      object: 'load_more_overnight',
      action: 'clicked',
      customerGroup: distributorId,
      customAttribute1: getLoggedInState(isLoggedIn),
    } as CustomerPortalEvent,

    NOTICE_CLICKED: {
      ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
      component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
      eventName: 'load_more_notice_clicked',
      object: 'load_more_notice',
      action: 'clicked',
      customerGroup: distributorId,
      customAttribute1: getLoggedInState(isLoggedIn),
    } as CustomerPortalEvent,
  },

  // ===================================================== //
  //          OFFER TABLE -  INDIVIDUAL SELECTORS
  // ===================================================== //
  TERM_FILTERED: ({ selectedTerm }) =>
    ({
      ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
      component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
      eventName: 'term_filtered',
      object: 'term',
      action: 'filtered',
      customerGroup: distributorId,
      customAttribute1: getLoggedInState(isLoggedIn),
      customAttribute2: selectedTerm,
    } as CustomerPortalEvent),

  RATING_FILTERED: {
    ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
    component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
    eventName: 'rating_filtered',
    object: 'rating',
    action: 'filtered',
    customerGroup: distributorId,
    customAttribute1: getLoggedInState(isLoggedIn),
  } as CustomerPortalEvent,

  INVESTMENT_AMOUNT_INPUTED: ({ investmentAmount, productType }) =>
    ({
      ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
      component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
      eventName: 'investment_amount_inputed',
      object: 'investment_amount',
      action: 'inputed',
      customerGroup: distributorId,
      customAttribute1: getLoggedInState(isLoggedIn),
      customAttribute2: investmentAmount,
      customAttribute3: productType,
    } as CustomerPortalEvent),

  COLUMN_SORTED: ({ selectedColumn, sortingOrder }) =>
    ({
      ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
      component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
      eventName: 'column_sorted',
      object: 'column',
      action: 'sorted',
      customerGroup: distributorId,
      customAttribute1: getLoggedInState(isLoggedIn),
      customAttribute2: selectedColumn,
      customAttribute3: sortingOrder,
    } as CustomerPortalEvent),

  COUNTRY_FILTERED: ({ selectedCountry }) =>
    ({
      ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
      component: CUSTOMER_PORTAL_COMPONENT.OFFER_TABLE,
      eventName: 'country_filtered',
      object: 'country',
      action: 'filtered',
      customerGroup: distributorId,
      customAttribute1: getLoggedInState(isLoggedIn),
      customAttribute2: selectedCountry,
    } as CustomerPortalEvent),
});

/**
 *
 * @param OFFER_TABLE_EVENTS
 * @returns  method to trigger event on Offers table row's tab Click.
 */
export const onOfferRowTabClickHandler =
  (OFFER_TABLE_EVENTS) => (tab: string, productId: string) => {
    trackEvent(OFFER_TABLE_EVENTS['OFFER_ROW'][`${tab}_CLICKED`]({ productId }), true);
  };

/**
 *
 * @param OFFER_TABLE_EVENTS
 * @returns  method to trigger event on Offers table Load More Click.
 */
export const onLoadMoreClickHandler = (OFFER_TABLE_EVENTS) => (productType: string) => {
  trackEvent(OFFER_TABLE_EVENTS['LOAD_MORE'][`${productType}_CLICKED`], true);
};

/**
 *
 * @param OFFER_TABLE_EVENTS
 * @returns method to trigger event on Offers table Tab Click.
 */
export const onSelectTab = (OFFER_TABLE_EVENTS) => (selectedTab: string) => {
  const selectedProductType = getSelectedProductTab(selectedTab);
  trackEvent(OFFER_TABLE_EVENTS['PRODUCT_TYPE'][`${selectedProductType}_CLICKED`], true);
};

/**
 *
 * @param OFFER_TABLE_EVENTS
 * @returns method to trigger event on Offers table term filter change.
 */
export const onTermFilterChangeHandler = (OFFER_TABLE_EVENTS) => (term: any) => {
  trackEvent(OFFER_TABLE_EVENTS.TERM_FILTERED({ selectedTerm: term }), true);
};

/**
 *
 * @param OFFER_TABLE_EVENTS
 * @returns method to trigger event on Offers table country filter change.
 */
export const onCountryFilterChangeHandler = (OFFER_TABLE_EVENTS) => (countries: any) => {
  trackEvent(OFFER_TABLE_EVENTS.COUNTRY_FILTERED({ selectedCountry: countries }), true);
};

/**
 *
 * @param OFFER_TABLE_EVENTS
 * @returns method to trigger event on Offers table investmentAmount input Change.
 */
export const onInvestmentAmountInputChangeHandler =
  (OFFER_TABLE_EVENTS) => (value: any, productType: string) => {
    trackEvent(
      OFFER_TABLE_EVENTS.INVESTMENT_AMOUNT_INPUTED({
        investmentAmount: value,
        productType: productType,
      }),
      true,
    );
  };

/**
 *
 * @param OFFER_TABLE_EVENTS
 * @returns method to trigger event on Offers table column's sort change.
 */
export const onColumnSortChangeHandler =
  (OFFER_TABLE_EVENTS) => (selectedColumn: any, sortingOrder: string) => {
    trackEvent(
      OFFER_TABLE_EVENTS.COLUMN_SORTED({
        selectedColumn: selectedColumn,
        sortingOrder: sortingOrder,
      }),
      true,
    );
  };

/**
 *
 * @param OFFER_TABLE_EVENTS
 * @returns method to trigger event on Offers table country credit rating change.
 */
export const onCountryRatingChangeHandler = (OFFER_TABLE_EVENTS) => () => {
  trackEvent(OFFER_TABLE_EVENTS.RATING_FILTERED, true);
};

/**
 *
 * @param OFFER_TABLE_EVENTS
 * @returns method to trigger event on Offers table Invest Now CTA Click.
 */
export const onCTAClickHandler = (OFFER_TABLE_EVENTS) => (productId, investmentAmount) => {
  trackEvent(
    OFFER_TABLE_EVENTS.CTA_CLICKED({
      productId: productId,
      investmentAmount: investmentAmount,
    }),
    true,
  );
};

/**
 *
 * @param  OFFER_TABLE_EVENTS
 * @returns method to track time for products/offers to be loaded.
 */
export const trackProductsRenderedDuration =
  (OFFER_TABLE_EVENTS) =>
  ({ pageUrl, renderStartTime, renderEndTime, renderDuration }) => {
    if (renderStartTime && renderEndTime && renderDuration) {
      trackEvent(
        OFFER_TABLE_EVENTS.PRODUCTS_RENDER_DURATION({
          pageUrl,
          renderStartTime,
          renderEndTime,
          renderDuration,
        }),
        true,
      );
    }
  };
